import { Link } from "react-router-dom";
import { articleContent } from "@/mocks/seoArticle";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

export default function ArticlePage() {
  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        <article className="w-full max-w-4xl mx-auto px-6 md:px-10 pt-32 pb-20">
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Article",
                "headline": articleContent.title,
                "description": articleContent.metaDescription,
                "datePublished": articleContent.datePublished,
                "author": { "@type": "Person", "name": articleContent.author },
                "publisher": { "@type": "Organization", "name": "ItaobuySpreadsheet", "logo": { "@type": "ImageObject", "url": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/62f99c0415358bd5388d5ed1df9ac1ff.jpeg" } },
                "mainEntityOfPage": { "@type": "WebPage", "@id": "https://itaobuy.cfd/itaobuyspreadsheet-guide" }
              })
            }}
          />
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": [
                  { "@type": "Question", "name": "What is ItaobuySpreadsheet?", "acceptedAnswer": { "@type": "Answer", "text": "It is a structured product sourcing spreadsheet designed for eCommerce sellers who source products from China, providing curated product data, supplier insights, and market trend information." } },
                  { "@type": "Question", "name": "Is the itaobuy sheet free to use?", "acceptedAnswer": { "@type": "Answer", "text": "Access details vary based on the level and update frequency. Check kaspreadsheet.com for current availability." } },
                  { "@type": "Question", "name": "How often is the data updated?", "acceptedAnswer": { "@type": "Answer", "text": "The spreadsheet is refreshed regularly to reflect current market conditions and trending products." } },
                  { "@type": "Question", "name": "Can I use it for my specific niche?", "acceptedAnswer": { "@type": "Answer", "text": "The spreadsheet covers a broad range of categories including apparel, accessories, bags, and more, making it suitable for most eCommerce niches." } },
                  { "@type": "Question", "name": "Is technical knowledge required?", "acceptedAnswer": { "@type": "Answer", "text": "No — if you can use a basic spreadsheet, you can use the itaobuy sheet effectively." } }
                ]
              })
            }}
          />
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                  { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://itaobuy.cfd/" },
                  { "@type": "ListItem", "position": 2, "name": articleContent.title, "item": "https://itaobuy.cfd/itaobuyspreadsheet-guide" }
                ]
              })
            }}
          />
          <nav className="mb-8">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors"
            >
              <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
              Back to Homepage
            </Link>
          </nav>

          <header className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-semibold rounded-full">
                SEO Guide
              </span>
              <span className="text-xs text-foreground-500">{articleContent.datePublished}</span>
              <span className="text-xs text-foreground-500">by {articleContent.author}</span>
            </div>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight">
              {articleContent.title}
            </h1>
          </header>

          <div className="mb-12 rounded-2xl overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Panoramic%20shot%20of%20a%20modern%20bright%20sourcing%20workspace%20with%20an%20ultrawide%20monitor%20displaying%20colorful%20spreadsheet%20dashboards%20and%20product%20trend%20visualizations%2C%20surrounded%20by%20curated%20fashion%20product%20samples%20on%20a%20warm%20oak%20desk%2C%20expansive%20window%20with%20natural%20daylight%2C%20clean%20professional%20aesthetic%2C%20editorial%20technology%20photography%20with%20airy%20atmosphere%20and%20gentle%20shadows&width=1200&height=600&seq=guide-hero-article-v2&orientation=landscape"
              alt="ItaobuySpreadsheet Complete Guide"
              className="w-full h-64 md:h-80 object-cover object-top"
            />
          </div>

          <div className="prose-custom">
            {articleContent.sections.map((section, idx) => (
              <section key={idx} className="mb-12">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                  {section.heading}
                </h2>
                {section.content.split("\n\n").map((paragraph, pIdx) => (
                  <p key={pIdx} className="text-foreground-700 leading-relaxed mb-4 text-base">
                    {paragraph}
                  </p>
                ))}
              </section>
            ))}
          </div>

          <div className="mt-16 p-8 bg-background-100 rounded-2xl border border-background-200/70">
            <h3 className="font-heading text-lg font-bold text-foreground-950 mb-3">
              Ready to Start Sourcing?
            </h3>
            <p className="text-foreground-600 text-sm mb-6">
              Visit the main platform to access the full itaobuy spreadsheet and start discovering profitable products today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 text-white rounded-lg text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                itaobuy sheet
                <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-foreground-950 text-white rounded-lg text-sm font-semibold hover:bg-foreground-800 transition-colors whitespace-nowrap"
              >
                buy it now
                <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
              </a>
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-foreground-300 text-foreground-700 rounded-lg text-sm font-semibold hover:border-foreground-950 hover:text-foreground-950 transition-colors whitespace-nowrap"
              >
                Back to Homepage
              </Link>
            </div>
          </div>

          <div className="mt-12 p-8 bg-background-100 rounded-2xl border border-background-200/70">
            <h3 className="font-heading text-lg font-bold text-foreground-950 mb-4">
              Frequently Asked Questions
            </h3>
            <div className="space-y-6">
              {[
                {
                  q: "What is ItaobuySpreadsheet?",
                  a: "It is a structured product sourcing spreadsheet designed for eCommerce sellers who source products from China, providing curated product data, supplier insights, and market trend information.",
                },
                {
                  q: "Is the itaobuy sheet free to use?",
                  a: "Access details vary based on the level and update frequency. Check kaspreadsheet.com for current availability.",
                },
                {
                  q: "How often is the data updated?",
                  a: "The spreadsheet is refreshed regularly to reflect current market conditions and trending products.",
                },
                {
                  q: "Can I use it for my specific niche?",
                  a: "The spreadsheet covers a broad range of categories including apparel, accessories, bags, and more, making it suitable for most eCommerce niches.",
                },
                {
                  q: "Is technical knowledge required?",
                  a: "No — if you can use a basic spreadsheet, you can use the itaobuy sheet effectively.",
                },
              ].map((faq, idx) => (
                <div key={idx}>
                  <h4 className="font-heading text-base font-bold text-foreground-950 mb-2">
                    Q{idx + 1}: {faq.q}
                  </h4>
                  <p className="text-foreground-600 text-sm">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-12 text-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 transition-colors font-medium"
            >
              <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
              Return to Homepage — Explore Product Categories
            </Link>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}